// RecommendedCompSelection is responsible for the overall comp selection user
// flow - allowing the user to query for recommended comps, waiting, and then
// showing comp selection (right now this is a grid).
import {
  AloftDate,
  CompType,
  LocationType,
  MLSListing,
  PolygonGeoSearch,
  UAD_RATINGS_IMAGE_ANALYSIS_API,
  ZillowPropertySearchResponse,
  isDefined,
  isUndefined,
  schSixTierQuality,
} from '@aloft/toolkit-shared';

import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';

import { fetchRunUADRatingsImageAnalysis } from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { useEdgeTrigger } from '../../../hooks/useEdgeTrigger.ts';
import { useZodFormContext } from '../../../hooks/useZodForm.ts';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { countActiveFilters } from '../../FilterDrawer/countActiveFilters.ts';
import { LoadingScreenSam } from '../../LoadingScreen/LoadingScreenSam.tsx';
import { useRecommendedProperties } from '../hooks/useRecommendedProperties.ts';
import { useSubjectPropertyToCompSelectionProperty } from '../hooks/useSubjectPropertyToCompSelectionProperty.ts';
import {
  CompSearch,
  CompSelectionProperty,
  RecommendCompsFormSchema,
  RecommendedCompSelectionProps,
} from '../types.ts';
import {
  NewSimilarityScoreCalcOptions,
  addSimilarityScores,
} from '../utils/addSimilarityScores.ts';
import {
  MAX_COMPS as RENTAL_MAX_COMPS,
  buildRentalRecommendationLists,
} from '../utils/buildRentalRecommendationLists.ts';
import { calculateCompSelectionStatistics } from '../utils/calculateCompSelectionStatistics.ts';
import { getCompSelectionMergeKey } from '../utils/compSelectionMatching.ts';
import { compsToCompSelectionProps } from '../utils/compsToCompSelectionProps.ts';
import { convertRecommendedCompsToCSV } from '../utils/convertRecommendedCompsToCSVData.ts';
import {
  filterByUadRatingFilters,
  resolveUadRatingRange,
} from '../utils/filterByUadRatingFilters.ts';
import { isWithinDistanceMiles } from '../utils/isWithinDistanceMiles.ts';
import { listingsToCompSelectionProps } from '../utils/listingsToCompSelectionProps.ts';
import { mergeSelectedComps } from '../utils/mergeSelectedComps.ts';
import { zillowPropsToCompSelectionProps } from '../utils/zillowPropsToCompSelectionProps.ts';
import { CompSelection } from './CompSelection.tsx';
import { MissingDataWarning } from './MissingDataWarning.tsx';
import { NoRecommendationsAvailable } from './NoRecommendationsAvailable.tsx';
import { getQualityTierBreakdownData } from './qualityTier/getQualityTierBreakdownData.ts';

// ─── Sales types & helpers ────────────────────────────────────────────────────

type ListingData =
  | { source: 'constellation1'; listings: MLSListing[] }
  | { source: 'abode'; listings: MLSListing[] }
  | { source: 'z'; listings: ZillowPropertySearchResponse[] };

const NEARBY_MILES_THRESHOLD = 100;
const MAX_COMPS = 99;

type UADRatingsRowState = Pick<
  CompSelectionProperty,
  | 'uadQualityRating'
  | 'uadConditionRating'
  | 'uadQualityRatingCommentary'
  | 'uadConditionRatingCommentary'
  | 'uadRatingsLoading'
  | 'uadRatingsSkipped'
  | 'uadQualityRatingFailed'
  | 'uadConditionRatingFailed'
>;

type UADAnalysisCandidate = CompSelectionProperty & { listingData: MLSListing };

const parseUADRating = (rating?: string) => {
  const value = rating ? Number(rating.replace(/^[CQ]/i, '')) : undefined;
  return schSixTierQuality.safeParse(value).data;
};

const canShowUADRatingsState = (comp: CompSelectionProperty): comp is UADAnalysisCandidate =>
  comp.id !== 'subject' &&
  comp.t !== 'z' &&
  (comp.uadQualityRating === undefined || comp.uadConditionRating === undefined) &&
  comp.listingData !== undefined &&
  comp.listingData.MLSOUID !== undefined &&
  comp.listingData.ListingId !== undefined;

const hasUADRatingsMedia = (comp: UADAnalysisCandidate) =>
  (comp.listingData.Media ?? []).some((media) => media.MediaURL !== undefined);

const getUADRatingsAnalysisKey = (comp: UADAnalysisCandidate) =>
  `${comp.id}:${comp.listingData.MLSOUID}:${comp.listingData.ListingId}`;

const hasCompletedUadRating = (state?: UADRatingsRowState) =>
  !!state &&
  state.uadRatingsLoading !== true &&
  (state.uadQualityRating !== undefined ||
    state.uadConditionRating !== undefined ||
    state.uadQualityRatingFailed === true ||
    state.uadConditionRatingFailed === true);

const useUADRatingsByRowID = (visibleData: CompSelectionProperty[]) => {
  const { fetcherOptions } = useToolkitContext();
  const { selectShouldAllowRecommendedCompsImageAnalysisUADRatings } = featuresSelectors;
  const imageAnalysisRatingsEnabled = useAppSelector(
    selectShouldAllowRecommendedCompsImageAnalysisUADRatings,
  );
  const [uadRatingsByRowID, setUadRatingsByRowID] = useState<Record<string, UADRatingsRowState>>(
    {},
  );
  const fetcherRef = useRef(fetcherOptions);
  fetcherRef.current = fetcherOptions;
  const visibleDataRef = useRef(visibleData);
  const ratingsRef = useRef<Record<string, UADRatingsRowState>>({});
  const analysisKey = visibleData
    .map((comp) => (canShowUADRatingsState(comp) ? getUADRatingsAnalysisKey(comp) : comp.id))
    .join('|');

  useEffect(() => {
    visibleDataRef.current = visibleData;
  }, [visibleData]);

  useEffect(() => {
    ratingsRef.current = uadRatingsByRowID;
  }, [uadRatingsByRowID]);

  useEffect(() => {
    if (!imageAnalysisRatingsEnabled) {
      setUadRatingsByRowID({});
      return;
    }

    const rowsToAnalyze = visibleDataRef.current;
    const { accessTokenFn, headers } = fetcherRef.current;
    const uadCandidates = rowsToAnalyze.filter(canShowUADRatingsState);
    const completed = ratingsRef.current;
    const compsNeedingAnalysis = uadCandidates.filter(
      (comp) => hasUADRatingsMedia(comp) && !hasCompletedUadRating(completed[comp.id]),
    );
    const compsMissingMedia = uadCandidates.filter((comp) => !hasUADRatingsMedia(comp));

    if (compsNeedingAnalysis.length === 0 && compsMissingMedia.length === 0) {
      return;
    }

    let isActive = true;
    const abortController = new AbortController();
    setUadRatingsByRowID((current) => {
      let changed = false;
      const next = { ...current };
      compsMissingMedia.forEach((comp) => {
        if (hasCompletedUadRating(current[comp.id])) return;
        next[comp.id] = {
          uadRatingsLoading: false,
          uadQualityRatingFailed: true,
          uadConditionRatingFailed: true,
        };
        changed = true;
      });
      compsNeedingAnalysis.forEach((comp) => {
        if (hasCompletedUadRating(current[comp.id])) return;
        if (current[comp.id]?.uadRatingsLoading === true) return;
        next[comp.id] = { ...current[comp.id], uadRatingsLoading: true };
        changed = true;
      });
      return changed ? next : current;
    });

    Promise.all(
      compsNeedingAnalysis.map(async (comp) => {
        try {
          const response = await fetchRunUADRatingsImageAnalysis(
            {
              accessTokenFn,
              headers,
              body: { listing: comp.listingData },
            },
            abortController.signal,
          );
          if (typeof response === 'object' && response !== null && 'error' in response) {
            throw new Error('UAD ratings failed');
          }

          const result = UAD_RATINGS_IMAGE_ANALYSIS_API.POST.RESPONSE.parse(response);
          const uadQualityRating =
            comp.uadQualityRating ?? parseUADRating(result.content.uad_quality_rating);
          const uadConditionRating =
            comp.uadConditionRating ?? parseUADRating(result.content.uad_condition_rating);

          if (isActive) {
            setUadRatingsByRowID((current) => ({
              ...current,
              [comp.id]: {
                uadQualityRating,
                uadConditionRating,
                uadQualityRatingCommentary: result.content.uad_quality_commentary,
                uadConditionRatingCommentary: result.content.uad_condition_commentary,
                uadRatingsLoading: false,
                uadQualityRatingFailed: uadQualityRating === undefined,
                uadConditionRatingFailed: uadConditionRating === undefined,
              },
            }));
          }
        } catch {
          if (isActive && !abortController.signal.aborted) {
            setUadRatingsByRowID((current) => ({
              ...current,
              [comp.id]: {
                uadRatingsLoading: false,
                uadQualityRatingFailed: true,
                uadConditionRatingFailed: true,
              },
            }));
          }
        }
      }),
    );

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [analysisKey, imageAnalysisRatingsEnabled]);

  return uadRatingsByRowID;
};

// ─── Sales inner component ────────────────────────────────────────────────────

type InnerSharedProps = Pick<
  RecommendedCompSelectionProps,
  | 'sorting'
  | 'setSorting'
  | 'rowSelection'
  | 'setRowSelection'
  | 'orderID'
  | 'updateCompsLabel'
  | 'onUpdateComps'
  | 'onDeselectAll'
  | 'onFilterOpen'
  | 'onColumnOrderChange'
  | 'columnOrder'
  | 'columnVisibility'
  | 'isFilterPanelOpen'
  | 'proximityRequirementMiles'
  | 'onQualityTierBreakdownDataChange'
  | 'onStatisticsChange'
> & {
  subjectComp: CompSelectionProperty;
  comps: CompSelectionProperty[];
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  effectiveDate: AloftDate;
  newCalcOptions?: NewSimilarityScoreCalcOptions;
};

const RecCompSelectionInner = forwardRef(
  (
    {
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      listingData,
      subjectComp,
      comps,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      effectiveDate,
      newCalcOptions,
      updateCompsLabel,
      onUpdateComps,
      onDeselectAll,
      onFilterOpen,
      onColumnOrderChange,
      columnOrder,
      columnVisibility,
      isFilterPanelOpen,
      proximityRequirementMiles,
      onQualityTierBreakdownDataChange,
      onStatisticsChange,
    }: InnerSharedProps & { listingData: ListingData },
    ref,
  ) => {
    const subjectCoordinates = useMemo(
      () =>
        subjectComp.latitude && subjectComp.longitude
          ? { lat: subjectComp.latitude, lng: subjectComp.longitude }
          : undefined,
      [subjectComp.latitude, subjectComp.longitude],
    );

    const recommendations = useMemo(
      () =>
        [
          ...(listingData.source === 'constellation1' || listingData.source === 'abode'
            ? listingsToCompSelectionProps(subjectComp, comps, listingData.listings)
            : zillowPropsToCompSelectionProps(
                subjectCoordinates,
                subjectComp,
                comps,
                listingData.listings,
              )),
        ].filter((rec) => {
          switch (rec.mlsStatus?.toLowerCase()) {
            case 'active':
            case 'pending':
            case 'sold':
            case 'recently_sold':
              return true;
            default:
              return false;
          }
        }),
      [listingData, subjectComp, comps, subjectCoordinates, hasArvOption, newCalcOptions],
    );

    // If enough comps are nearby subject, filter out comps that aren't nearby
    const filteredRecs = useMemo(
      () =>
        (recommendations.filter(isWithinDistanceMiles(NEARBY_MILES_THRESHOLD)).length >= MAX_COMPS
          ? recommendations.filter(isWithinDistanceMiles(NEARBY_MILES_THRESHOLD))
          : recommendations
        ).filter(
          // Hack - simple but incomplete equality checker to filter out subject listing
          (comp) =>
            comp.address?.AddressLineText !== subjectComp.address?.AddressLineText &&
            (listingData.source === 'z' || comp.listingID !== subjectComp.listingID),
        ),
      [recommendations],
    );

    // Comps resolved from the "Manually Add Comps" modal (opened from within this table) that don't already exist as a recommendation.
    // Kept in local state so they render/select in the table without being persisted until "Update comps".
    const [manualComps, setManualComps] = useState<CompSelectionProperty[]>([]);

    const dataWithSelectedComps = useMemo(
      () => mergeSelectedComps(filteredRecs, [...comps, ...manualComps]),
      [comps, filteredRecs, manualComps],
    );

    const resolveManualComp = useCallback(
      (property: CompSelectionProperty, compType?: CompType) => {
        const mergeKey = getCompSelectionMergeKey(property);
        const existing = dataWithSelectedComps.find(
          (comp) => getCompSelectionMergeKey(comp) === mergeKey,
        );
        const id = existing?.id ?? mergeKey;

        if (!existing) {
          setManualComps((prev) =>
            prev.some((comp) => getCompSelectionMergeKey(comp) === mergeKey)
              ? prev
              : [...prev, { ...property, id: mergeKey }],
          );
        }

        setRowSelection((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            aiv: prev[id]?.aiv || compType !== 'arv',
            arv: prev[id]?.arv || compType === 'arv',
          },
        }));

        return id;
      },
      [dataWithSelectedComps, setRowSelection],
    );

    const [isExpanded, setIsExpanded] = useState(false);
    const { control } = useZodFormContext<RecommendCompsFormSchema>();
    const appliedSearch = useWatch({ control, name: 'originalSearch' });
    const addedCompIds = useMemo(() => new Set(comps.map((comp) => comp.id)), [comps]);

    const qualityRange = useMemo(
      () =>
        resolveUadRatingRange(
          appliedSearch?.filters?.quality,
          appliedSearch?.matchCriteria?.quality,
          subjectComp.uadQualityRating,
        ),
      [
        appliedSearch?.filters?.quality,
        appliedSearch?.matchCriteria?.quality,
        subjectComp.uadQualityRating,
      ],
    );
    const conditionRange = useMemo(
      () =>
        resolveUadRatingRange(
          appliedSearch?.filters?.condition,
          appliedSearch?.matchCriteria?.condition,
          subjectComp.uadConditionRating,
        ),
      [
        appliedSearch?.filters?.condition,
        appliedSearch?.matchCriteria?.condition,
        subjectComp.uadConditionRating,
      ],
    );
    const hasUadRatingFilters = qualityRange !== undefined || conditionRange !== undefined;

    const rowsForUad = useMemo(
      () => dataWithSelectedComps.slice(0, MAX_COMPS),
      [dataWithSelectedComps],
    );

    const handleExpand = useCallback(() => setIsExpanded(true), []);
    const handleCollapse = useCallback(() => setIsExpanded(false), []);
    const uadRatingsByRowID = useUADRatingsByRowID(rowsForUad);

    const filteredDataWithUADRatings = useMemo(() => {
      const withRatings = rowsForUad.map((comp) => ({
        ...comp,
        ...uadRatingsByRowID[comp.id],
      }));
      addSimilarityScores(withRatings, subjectComp, newCalcOptions);
      withRatings.sort(
        (a, b) => (b.similarityScore ?? Number.MIN_VALUE) - (a.similarityScore ?? Number.MIN_VALUE),
      );

      return filterByUadRatingFilters(withRatings, {
        qualityFilter: appliedSearch?.filters?.quality,
        conditionFilter: appliedSearch?.filters?.condition,
        qualityMatchCriteria: appliedSearch?.matchCriteria?.quality,
        conditionMatchCriteria: appliedSearch?.matchCriteria?.condition,
        subjectQuality: subjectComp.uadQualityRating,
        subjectCondition: subjectComp.uadConditionRating,
        keepIds: addedCompIds,
      }).map((property, index) => ({ ...property, similarityRank: index + 1 }));
    }, [
      addedCompIds,
      appliedSearch?.filters?.condition,
      appliedSearch?.filters?.quality,
      appliedSearch?.matchCriteria?.condition,
      appliedSearch?.matchCriteria?.quality,
      rowsForUad,
      subjectComp,
      newCalcOptions,
      subjectComp.uadConditionRating,
      subjectComp.uadQualityRating,
      uadRatingsByRowID,
    ]);

    const dataWithUADRatings = useMemo(
      () => filteredDataWithUADRatings.slice(0, MAX_COMPS),
      [filteredDataWithUADRatings],
    );

    const totalComps = Math.min(
      hasUadRatingFilters ? filteredDataWithUADRatings.length : dataWithSelectedComps.length,
      MAX_COMPS,
    );
    const downloadCSV = () => {
      const csvContent = convertRecommendedCompsToCSV(dataWithSelectedComps);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recommended_comps_order_${orderID}.csv`);
      link.style.visibility = 'hidden';
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    };

    const propertiesWithUadRatings = useMemo(
      () =>
        dataWithSelectedComps.map((comp) => ({
          ...comp,
          ...uadRatingsByRowID[comp.id],
        })),
      [dataWithSelectedComps, uadRatingsByRowID],
    );

    const getQualityTiersBreakdown = () => getQualityTierBreakdownData(propertiesWithUadRatings);
    const getRowsWithUADRatings = () => dataWithUADRatings;

    useEffect(() => {
      onQualityTierBreakdownDataChange?.(getQualityTierBreakdownData(propertiesWithUadRatings));
    }, [onQualityTierBreakdownDataChange, propertiesWithUadRatings]);

    useEffect(() => {
      onStatisticsChange?.(calculateCompSelectionStatistics(dataWithSelectedComps));
    }, [dataWithSelectedComps, onStatisticsChange]);

    useImperativeHandle(ref, () => ({
      downloadCSV,
      getQualityTiersBreakdown,
      getRowsWithUADRatings,
      resolveManualComp,
    }));

    return (
      <CompSelection
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={dataWithUADRatings}
        subject={subjectComp}
        comps={comps}
        source={listingData.source}
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        effectiveDate={effectiveDate}
        isExpanded={isExpanded}
        totalComps={totalComps}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
        updateCompsLabel={updateCompsLabel}
        onUpdateComps={onUpdateComps}
        onDeselectAll={onDeselectAll}
        onFilterOpen={onFilterOpen}
        onColumnOrderChange={onColumnOrderChange}
        columnOrder={columnOrder}
        columnVisibility={columnVisibility}
        isFilterPanelOpen={isFilterPanelOpen}
        proximityRequirementMiles={proximityRequirementMiles}
      />
    );
  },
);

// ─── Rental inner component ───────────────────────────────────────────────────

const RentalRecCompSelectionInner = forwardRef(
  (
    {
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      listings,
      subjectComp,
      comps,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      effectiveDate,
      newCalcOptions,
      updateCompsLabel,
      onUpdateComps,
      onDeselectAll,
      onFilterOpen,
      onColumnOrderChange,
      columnOrder,
      columnVisibility,
      isFilterPanelOpen,
      proximityRequirementMiles,
      onStatisticsChange,
    }: InnerSharedProps & { listings: MLSListing[] },
    ref,
  ) => {
    const selectedRecommendationsByIdRef = useRef<Record<string, CompSelectionProperty>>({});
    // Comps resolved from the "Manually Add Comps" modal that aren't already present among the rental recommendations/current comps.
    const [manualComps, setManualComps] = useState<CompSelectionProperty[]>([]);

    const { recommendations, filteredRecs } = useMemo(
      () =>
        buildRentalRecommendationLists(subjectComp, comps, listings, hasArvOption, newCalcOptions),
      [listings, subjectComp, comps, hasArvOption, newCalcOptions],
    );

    const dataWithSelectedComps = useMemo(() => {
      const seen = new Set<string>();
      const isSelected = (id: string) => {
        const selection = rowSelection[id];
        if (!selection) return false;
        if (!isInspectifyOrder || !hasArvOption) return true;
        return !!selection.aiv || !!selection.arv;
      };

      const nextSelectedRecommendationsById = { ...selectedRecommendationsByIdRef.current };
      Object.keys(nextSelectedRecommendationsById).forEach((id) => {
        if (!isSelected(id)) {
          delete nextSelectedRecommendationsById[id];
        }
      });
      [...comps, ...recommendations, ...manualComps].forEach((c) => {
        if (isSelected(c.id)) {
          nextSelectedRecommendationsById[c.id] = c;
        }
      });
      selectedRecommendationsByIdRef.current = nextSelectedRecommendationsById;

      const unique = (items: CompSelectionProperty[]) =>
        items.filter((c) => {
          const key = getCompSelectionMergeKey(c);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      const remainingFiltered = unique(filteredRecs);
      const selectedUnique = unique(comps);
      const selectedFromRecommendations = unique(Object.values(nextSelectedRecommendationsById));

      return mergeSelectedComps(
        [...selectedUnique, ...selectedFromRecommendations],
        remainingFiltered,
      );
    }, [
      comps,
      filteredRecs,
      recommendations,
      manualComps,
      rowSelection,
      isInspectifyOrder,
      hasArvOption,
    ]);

    const resolveManualComp = useCallback(
      (property: CompSelectionProperty) => {
        const mergeKey = getCompSelectionMergeKey(property);
        const existing = dataWithSelectedComps.find(
          (comp) => getCompSelectionMergeKey(comp) === mergeKey,
        );
        const id = existing?.id ?? mergeKey;

        if (!existing) {
          setManualComps((prev) =>
            prev.some((comp) => getCompSelectionMergeKey(comp) === mergeKey)
              ? prev
              : [...prev, { ...property, id: mergeKey }],
          );
        }

        setRowSelection((prev) => ({ ...prev, [id]: { ...prev[id] } }));

        return id;
      },
      [dataWithSelectedComps, setRowSelection],
    );

    const downloadCSV = () => {
      const csvContent = convertRecommendedCompsToCSV(dataWithSelectedComps);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recommended_rental_comps_order_${orderID}.csv`);
      link.style.visibility = 'hidden';
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    };

    useImperativeHandle(ref, () => ({
      downloadCSV,
      getQualityTiersBreakdown: () => ({}),
      resolveManualComp,
    }));

    const [isExpanded, setIsExpanded] = useState(false);

    const totalComps = Math.min(dataWithSelectedComps.length, RENTAL_MAX_COMPS);
    const visibleData = useMemo(
      () => dataWithSelectedComps.slice(0, RENTAL_MAX_COMPS),
      [dataWithSelectedComps],
    );

    useEffect(() => {
      onStatisticsChange?.(calculateCompSelectionStatistics(dataWithSelectedComps));
    }, [dataWithSelectedComps, onStatisticsChange]);

    const handleExpand = useCallback(() => setIsExpanded(true), []);
    const handleCollapse = useCallback(() => setIsExpanded(false), []);

    return (
      <CompSelection
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={visibleData}
        subject={subjectComp}
        comps={comps}
        source="abode"
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        effectiveDate={effectiveDate}
        compSelectionTableVariant="rental"
        isExpanded={isExpanded}
        totalComps={totalComps}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
        updateCompsLabel={updateCompsLabel}
        onUpdateComps={onUpdateComps}
        onDeselectAll={onDeselectAll}
        onFilterOpen={onFilterOpen}
        onColumnOrderChange={onColumnOrderChange}
        columnOrder={columnOrder}
        columnVisibility={columnVisibility}
        isFilterPanelOpen={isFilterPanelOpen}
        proximityRequirementMiles={proximityRequirementMiles}
      />
    );
  },
);

// ─── Shared outer component ───────────────────────────────────────────────────

export const RecommendedCompSelection = memo(
  forwardRef(
    (
      {
        compType = 'sales',
        orderID,
        subject,
        effectiveDate,
        comps,
        compSeq,
        getDefaultFormValues,
        showNoRecommendations,
        setShowNoRecommendations,
        sorting,
        setSorting,
        rowSelection,
        setRowSelection,
        isInspectifyOrder,
        hasArvOption,
        allowRecommendedCompsFiltering,
        onClose,
        allowNewSimilarityScore,
        similarityScoreWeights,
        updateCompsLabel,
        onUpdateComps,
        onDeselectAll,
        onFilterOpen,
        onColumnOrderChange,
        columnOrder,
        columnVisibility,
        isFilterPanelOpen,
        proximityRequirementMiles,
        onQualityTierBreakdownDataChange,
        onStatisticsChange,
      }: RecommendedCompSelectionProps,
      ref,
    ) => {
      const isRental = compType === 'rental';

      const effectiveDateAloft = useMemo(
        () => AloftDate.fromIsoString(effectiveDate.isoString),
        [effectiveDate.isoString],
      );
      const recommendedProperties = useRecommendedProperties({ orderID, compType });
      const { isIdle, isLoading, isError, data, getProperties } = recommendedProperties;

      const subjectComp = useSubjectPropertyToCompSelectionProperty(subject, effectiveDateAloft);

      const subjectLocationType: LocationType =
        subject.editableData.propertyIdentification.locationType ?? 'Urban';
      const locationTypeWeights = similarityScoreWeights?.[subjectLocationType];

      const newCalcOptions = useMemo(
        (): NewSimilarityScoreCalcOptions | undefined =>
          allowNewSimilarityScore && locationTypeWeights
            ? { weights: locationTypeWeights, effectiveDate: effectiveDateAloft }
            : undefined,
        [allowNewSimilarityScore, effectiveDateAloft, locationTypeWeights],
      );

      const convertedComps = useMemo(
        () => compsToCompSelectionProps(compSeq, comps, subjectComp).filter(isDefined),
        [compSeq, comps, newCalcOptions, subjectComp],
      );

      const listingData = useMemo((): ListingData | undefined => {
        if (!data) return undefined;
        const results = data.body?.status === 'found' ? (data.body?.output?.results ?? []) : [];
        if (data.source === 'constellation1' || data.source === 'abode') {
          return { source: data.source, listings: results as MLSListing[] };
        }
        if (data.source === 'z') {
          return { source: data.source, listings: results as ZillowPropertySearchResponse[] };
        }
        return undefined;
      }, [data]);

      const rentalListings = useMemo((): MLSListing[] => {
        if (
          data?.source === 'abode' &&
          data.body &&
          'status' in data.body &&
          data.body.status === 'found' &&
          'output' in data.body &&
          data.body.output &&
          'results' in data.body.output
        ) {
          return data.body.output.results;
        }
        return [];
      }, [data]);

      const isSubjectGlaDefined = isDefined(subject.editableData.improvements.gla);
      const isSubjectLotSizeDefined =
        isDefined(subject.editableData.site.area) && subject.editableData.site.area.unitAmount > 0;
      const isSubjectLocationDefined =
        isDefined(subjectComp.latitude) && isDefined(subjectComp.longitude);
      const { getValues, setValue } = useZodFormContext<RecommendCompsFormSchema>();

      const isRunnable = isSubjectGlaDefined && isSubjectLotSizeDefined && isSubjectLocationDefined;

      const getSearchCriteria = () => {
        const compSearch = getValues().search as CompSearch;
        const coordinates = (getValues().search.geoSearch as PolygonGeoSearch).coords;
        return { search: compSearch, coordinates };
      };

      useEdgeTrigger({
        condition: !isLoading && isIdle && isRunnable && isUndefined(data),
        action: () => {
          const { search } = getValues();
          if (countActiveFilters(search.filters) === 0) {
            const formSearch = getDefaultFormValues();
            setValue('search', formSearch);
          }
          setShowNoRecommendations(false);
          getProperties(getSearchCriteria());
        },
      });

      useEffect(() => {
        const status = data?.body?.status;
        if (status === 'not_found' || status === 'error') setShowNoRecommendations(true);
        else setShowNoRecommendations(false);
      }, [data, setShowNoRecommendations]);

      if (
        !isInspectifyOrder &&
        showNoRecommendations &&
        (data?.body?.status === 'not_found' || data?.body?.status === 'error')
      ) {
        return (
          <NoRecommendationsAvailable
            body={data.body}
            getProperties={() => {
              if (!isRental && allowRecommendedCompsFiltering) setShowNoRecommendations(false);
              else {
                getProperties(getSearchCriteria());
                setShowNoRecommendations(false);
              }
            }}
          />
        );
      }

      if (isError) {
        return (
          <NoRecommendationsAvailable
            body={{ status: 'error', message: 'An internal error occurred.' }}
            getProperties={() => {
              setShowNoRecommendations(false);
              getProperties(getSearchCriteria());
            }}
          />
        );
      }

      if (isLoading || !data) {
        if (!isLoading && !isRunnable && !data) {
          return (
            <MissingDataWarning
              isSubjectGlaDefined={isSubjectGlaDefined}
              isSubjectLotSizeDefined={isSubjectLotSizeDefined}
              isSubjectLocationDefined={isSubjectLocationDefined}
              onClose={onClose}
            />
          );
        }

        return (
          <LoadingScreenSam
            overrideText={
              isRental ? 'Loading recommended rental comps...' : 'Loading recommended comps...'
            }
          />
        );
      }

      const sharedInnerProps = {
        sorting,
        setSorting,
        rowSelection,
        setRowSelection,
        subjectComp,
        comps: convertedComps,
        orderID,
        isInspectifyOrder,
        hasArvOption,
        effectiveDate: effectiveDateAloft,
        newCalcOptions,
        updateCompsLabel,
        onUpdateComps,
        onDeselectAll,
        onFilterOpen,
        onColumnOrderChange,
        columnOrder,
        columnVisibility,
        isFilterPanelOpen,
        proximityRequirementMiles,
        onQualityTierBreakdownDataChange,
        onStatisticsChange,
        ref,
      };

      if (isRental) {
        return <RentalRecCompSelectionInner {...sharedInnerProps} listings={rentalListings} />;
      }

      if (!listingData) {
        throw new Error('Invalid source');
      }

      return <RecCompSelectionInner {...sharedInnerProps} listingData={listingData} />;
    },
  ),
);
